import { useState } from 'react';
import { Alert, Button, Form, Modal } from 'react-bootstrap';
import { LINE_TO_JIT_REASON_LIMIT } from '@/infrastructure/FulfillmentAdminClient';
import { ULTIMATE_MODAL } from '@/pages/fulfillment/components/ultimateModal';

/** What the dialog sends; `quantity` is absent when the whole line switches. */
export interface SwitchLineToJitAsk {
  quantity?: number;
  reason: string;
}

interface SwitchLineToJitModalProps {
  readonly line: { readonly lineNo: number; readonly sku: string; readonly totalQuantity: number };
  readonly busy: boolean;
  /** The server's last refusal of this switch, shown until the operator sends again. */
  readonly refusal?: string | undefined;
  readonly onHide: () => void;
  readonly onConfirm: (ask: SwitchLineToJitAsk) => void;
}

const TITLE_ID = 'switch-line-to-jit-title';

/**
 * "No stock → awaiting purchase" (SSD-7506): the shelf the line was booked from does not have the units, so the order
 * team switches the line, or the missing part of it, to purchase. The server takes those units off the shelf in a
 * stock adjustment and the line waits for a purchase like any JIT line; the reason is kept on both.
 */
export function SwitchLineToJitModal({ line, busy, refusal, onHide, onConfirm }: SwitchLineToJitModalProps) {
  const [quantityText, setQuantityText] = useState(String(line.totalQuantity));
  const [reason, setReason] = useState('');

  const quantity = Number(quantityText.trim());
  const quantityValid = Number.isInteger(quantity) && quantity >= 1 && quantity <= line.totalQuantity;
  const trimmedReason = reason.trim();
  const partial = quantityValid && quantity < line.totalQuantity;
  const block = !quantityValid
    ? `Quantity must be a whole number from 1 to ${line.totalQuantity}.`
    : trimmedReason === ''
      ? 'Say why the units are missing.'
      : '';

  return (
    <Modal {...ULTIMATE_MODAL} show onHide={onHide} centered aria-labelledby={TITLE_ID}>
      <Modal.Header closeButton>
        <Modal.Title id={TITLE_ID}>
          No stock → awaiting purchase — {line.sku} (line {line.lineNo})
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="small text-muted">
          For units this line counts from existing inventory that are not on the shelf. They are taken off the shelf in
          a stock adjustment and the line waits for a purchase instead.
        </p>
        <Form.Group className="mb-3" controlId="switch-line-to-jit-quantity">
          <Form.Label className="fw-semibold">Missing quantity</Form.Label>
          <Form.Control
            type="number"
            min={1}
            max={line.totalQuantity}
            value={quantityText}
            isInvalid={quantityText.trim() !== '' && !quantityValid}
            onChange={(event) => setQuantityText(event.target.value)}
          />
          <Form.Text muted>
            {partial
              ? `Line ${line.lineNo} keeps ${line.totalQuantity - quantity} unit(s) from stock; the ${quantity} missing unit(s) move to a new line awaiting purchase.`
              : `Default = the whole line (${line.totalQuantity} unit(s)).`}
          </Form.Text>
        </Form.Group>
        <Form.Group controlId="switch-line-to-jit-reason">
          <Form.Label className="fw-semibold">Reason</Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            maxLength={LINE_TO_JIT_REASON_LIMIT}
            placeholder="e.g. FC counted 0 on 10-19"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
          <Form.Text muted>Kept on the order history and on the stock adjustment.</Form.Text>
        </Form.Group>
        {refusal && (
          <Alert variant="danger" className="mt-3 mb-0">
            {refusal}
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide}>
          Keep the line
        </Button>
        <Button
          variant="danger"
          disabled={busy || block !== ''}
          title={block}
          onClick={() => onConfirm({ ...(partial ? { quantity } : {}), reason: trimmedReason })}
        >
          Switch to purchase
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
